const CAROUSEL_CONSTANTS = {
  CAROUSEL_WIDTH: 140,
  EAGER_LOAD_LIMIT: 10,
  RETRY_DELAY_BASE: 120,
  MAX_RETRY_DELAY: 500,
  REFRESH_DEBOUNCE: 100,
  INIT_DELAY: 100,
  SYNC_DELAY: 100,
  PROGRESSIVE_SCALE_LEVELS: 5,
  PROGRESSIVE_SCALE_DELAY: 180,
  PLACEHOLDER_SVG: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="630" height="900"%3E%3Crect width="100%25" height="100%25" fill="%23f0f0f0"/%3E%3C/svg%3E'
};

export function galleryCarousel() {
  return {
    // COMPONENT STATE PROPERTIES
    $slick: null,
    isInitialized: false,
    imageObserver: null,
    targetArtworkId: null,
    pendingSyncTimeout: null,
    pendingSyncAttempts: 0,
    maxPendingSyncAttempts: 10,
    _primeHandler: null,
    _pendingSyncSlide: null,
    carousel_ready: false,
    _refreshTimeout: null,
    _lastRefreshOrientation: null,

    // CONFIGURATION
    slickConfig: {
      infinite: true,
      centerMode: true,
      variableWidth: false,
      focusOnSelect: true,
      slidesToShow: 9,
      slidesToScroll: 1,
      centerPadding: '0px',
      speed: 200,
      cssEase: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      useCSS: true,
      useTransform: true,
      arrows: false,
      dots: false,
      swipe: true,
      swipeToSlide: true,
      touchMove: true,
      touchThreshold: 8,
      draggable: true,
      edgeFriction: 0.15,
      accessibility: true,
      adaptiveHeight: false,
      waitForAnimate: false,
      responsive: [
        {
          breakpoint: 990,
          settings: {
            speed: 200,
            swipe: true,
            swipeToSlide: true,
            touchMove: true,
            touchThreshold: 8
          }
        }
      ]
    },

    // COMPUTED PROPERTIES & GETTERS
    get isCarouselReady() {
      return this.isInitialized && this.$slick && this.$refs.slickContainer;
    },

    get slickInstance() {
      return this.$slick ? this.$slick.slick('getSlick') : null;
    },

    get filtered_artworks() {
      return this.$store.galleryProducts.filtered_artworks;
    },

    get selected_artworks() {
      return this.$store.galleryCore.selected_artworks;
    },

    get ui_state() {
      return {
        active_artwork_index: this.$store.galleryUI.active_artwork_index,
        hover_artwork_index: this.$store.galleryUI.hover_artwork_index
      };
    },

    get orientation() {
      return this.$store.galleryCore.orientation;
    },

    get is_loading() {
      const uiStore = this.$store.galleryUI;
      return Boolean(uiStore.searching || uiStore.backgroundLoading);
    },

    // UTILITY METHODS
    clearTimeout(timeoutRef) {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
        return null;
      }
      return null;
    },

    getPlaceholderUrl(width = CAROUSEL_CONSTANTS.CAROUSEL_WIDTH) {
      return `${CAROUSEL_CONSTANTS.PLACEHOLDER_SVG}?width=${width}`;
    },

    getExhibitionBlackBorderImageUrl(artwork) {
      if (!artwork) {
        console.warn('[Desktop Carousel] No artwork provided');
        return this.getPlaceholderUrl(88);
      }

      if (artwork.variants && Array.isArray(artwork.variants)) {
        const exhibitionVariant = artwork.variants.find(v => {
          return v.option1 === 'Exhibition' &&
                 v.option2 === 'Black' &&
                 v.option3 === 'Border';
        });

        if (exhibitionVariant?.featured_image?.src) {
          const baseUrl = exhibitionVariant.featured_image.src.split('?')[0];
          return `${baseUrl}?width=${CAROUSEL_CONSTANTS.CAROUSEL_WIDTH}`;
        }
      }
      
      const imageUrl = artwork._base_image_source || artwork.image_url || artwork.featured_image?.src;
      if (imageUrl) {
        const baseUrl = imageUrl.split('?')[0];
        return `${baseUrl}?width=${CAROUSEL_CONSTANTS.CAROUSEL_WIDTH}`;
      }

      return artwork.original_thumbnail_url || this.getPlaceholderUrl();
    },

    isArtworkSelected(artwork) {
      if (!artwork || !artwork.id) return false;
      return this.selected_artworks.find(s => s && s.id === artwork.id) !== undefined;
    },

    isArtworkHorizontal(artwork) {
      if (!artwork) return true;

      if (artwork.tags) {
        const tagsArray = Array.isArray(artwork.tags) ? artwork.tags :
                          (typeof artwork.tags === 'string' ? [artwork.tags] : []);

        for (const tag of tagsArray) {
          if (tag === 'Orientation|Horizontal' || tag === 'orientation-horizontal' ||
              (typeof tag === 'string' && (tag.includes('Orientation|Horizontal') || tag.includes('orientation-horizontal')))) {
            return true;
          }
          if (tag === 'Orientation|Vertical' || tag === 'orientation-vertical' ||
              (typeof tag === 'string' && (tag.includes('Orientation|Vertical') || tag.includes('orientation-vertical')))) {
            return false;
          }
        }
      }

      if (artwork.featured_image?.width && artwork.featured_image?.height) {
        return artwork.featured_image.width > artwork.featured_image.height;
      }

      return true;
    },

    findSlideIndexByArtworkId(artworkId) {
      if (!artworkId || !Array.isArray(this.filtered_artworks)) {
        return -1;
      }

      return this.filtered_artworks.findIndex((artwork) => artwork?.id === artworkId);
    },

    // IMAGE OBSERVER
    initImageObserver() {
      if (!('IntersectionObserver' in window)) {
        return;
      }

      this.imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src && img.src !== img.dataset.src) {
              const tempImg = new Image();
              tempImg.onload = () => {
                img.classList.add('gallery-slide__image--loading');
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
                setTimeout(() => {
                  img.classList.remove('gallery-slide__image--loading');
                }, 300);
              };
              tempImg.src = img.dataset.src;
            }
            this.imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });
    },

    // SLIDE CREATION & MANAGEMENT
    createSlides() {
      const slickContainer = this.$refs.slickContainer;

      if (!slickContainer || !this.filtered_artworks?.length) return;

      if (!this.imageObserver) {
        this.initImageObserver();
      }

      const placeholderHorizontal = slickContainer.dataset.placeholderHorizontal || '';
      const placeholderVertical = slickContainer.dataset.placeholderVertical || '';

      slickContainer.innerHTML = '';

      this.filtered_artworks.filter(artwork => artwork && artwork.id).forEach((artwork, index) => {
        const slideDiv = document.createElement('div');
        slideDiv.className = 'gallery-slide';
        slideDiv.setAttribute('data-artwork-id', artwork.id);
        slideDiv.setAttribute('data-slide-index', index);
        slideDiv.setAttribute('role', 'button');
        slideDiv.setAttribute('tabindex', '0');
        slideDiv.setAttribute('aria-label', `Select ${artwork.title} by ${artwork.artist || 'Unknown Artist'}`);

        if (this.isArtworkSelected(artwork)) {
          slideDiv.classList.add('gallery-slide--selected');
        }

        const slideContainer = document.createElement('div');
        slideContainer.className = 'gallery-slide__container';

        const img = document.createElement('img');
        const imageUrl = this.getExhibitionBlackBorderImageUrl(artwork);

        const isHorizontal = this.isArtworkHorizontal(artwork);
        const placeholderUrl = isHorizontal ? placeholderHorizontal : placeholderVertical;

        if (index < CAROUSEL_CONSTANTS.EAGER_LOAD_LIMIT) {
          img.src = imageUrl;
          img.loading = 'eager';
        } else {
          img.src = placeholderUrl || imageUrl;
          img.loading = 'lazy';
          img.dataset.src = imageUrl;

          if (this.imageObserver) {
            this.imageObserver.observe(img);
          }
        }

        if (placeholderUrl) {
          img.onerror = function() {
            this.onerror = null;
            this.src = placeholderUrl;
          };
        }

        img.alt = artwork.title;
        img.className = 'gallery-slide__image';
        img.width = CAROUSEL_CONSTANTS.CAROUSEL_WIDTH;
        img.height = CAROUSEL_CONSTANTS.CAROUSEL_WIDTH;

        slideContainer.appendChild(img);
        slideDiv.appendChild(slideContainer);
        slickContainer.appendChild(slideDiv);
      });
    },

    updateSlideSelectionClasses() {
      if (!this.isCarouselReady) return;

      const slickInstance = this.slickInstance;
      if (!slickInstance || !slickInstance.$slides) return;

      const activeIndex = this.ui_state.active_artwork_index;
      const activeArtworkId = activeIndex !== null && this.selected_artworks[activeIndex]
        ? this.selected_artworks[activeIndex].id
        : null;

      slickInstance.$slides.each((index, slideElement) => {
        const $slide = $(slideElement);
        const artworkIdStr = $slide.attr('data-artwork-id');

        if (artworkIdStr && activeArtworkId !== null) {
          const artworkIdNum = parseInt(artworkIdStr, 10);
          const isActiveArtwork = activeArtworkId === artworkIdNum ||
                                  activeArtworkId === artworkIdStr ||
                                  String(activeArtworkId) === artworkIdStr;

          if (isActiveArtwork) {
            $slide.addClass('gallery-slide--selected');
          } else {
            $slide.removeClass('gallery-slide--selected');
          }
        } else {
          $slide.removeClass('gallery-slide--selected');
        }
      });
    },

    updateProgressiveScaling(slick, currentSlide) {
      const totalSlides = slick.slideCount;

      for (let i = 0; i < totalSlides; i++) {
        const slide = slick.$slides.eq(i);

        let distance = Math.abs(i - currentSlide);
        if (distance > totalSlides / 2) {
          distance = totalSlides - distance;
        }

        const scaleLevel = Math.min(distance, CAROUSEL_CONSTANTS.PROGRESSIVE_SCALE_LEVELS);

        slide.removeClass((_index, className) => {
          return (className.match(/(^|\s)gallery-slide--distance-\S+/g) || []).join(' ');
        });

        slide.addClass(`gallery-slide--distance-${scaleLevel}`)
             .attr('data-distance', scaleLevel);
      }

      setTimeout(() => {
        if (slick && slick.setPosition) {
          slick.setPosition();
        }
      }, CAROUSEL_CONSTANTS.PROGRESSIVE_SCALE_DELAY);
    },

    // SLICK CAROUSEL INITIALIZATION
    initSlick() {
      if (this.isInitialized || !this.$refs.slickContainer) {
        return;
      }

      if (typeof window.$ === 'undefined' || typeof window.jQuery === 'undefined') {
        setTimeout(() => this.initSlick(), CAROUSEL_CONSTANTS.INIT_DELAY);
        return;
      }

      this.createSlides();
      this.$slick = window.$(this.$refs.slickContainer);
      this.$slick.slick(this.slickConfig);
      this.setupSlickEvents();
      this.isInitialized = true;
      this.carousel_ready = true;

      this.requestSyncToTarget(0);
    },

    setupSlickEvents() {
      this._afterChangeHandler = (_event, slick, currentSlide) => {
        this.updateProgressiveScaling(slick, currentSlide);

        if (this._pendingSyncSlide !== null && this._pendingSyncSlide === currentSlide) {
          this._pendingSyncSlide = null;
        }
      };

      this._initHandler = (_event, slick) => {
        const initialSlide = Math.floor(slick.slideCount / 2);
        this.updateProgressiveScaling(slick, initialSlide);

        setTimeout(() => {
          this.$slick.slick('slickGoTo', initialSlide);
          this.requestSyncToTarget();
        }, CAROUSEL_CONSTANTS.SYNC_DELAY);
      };

      this._clickHandler = (event) => {
        const clickedSlide = $(event.currentTarget);
        const slideIndex = clickedSlide.data('slick-index');
        this.handleSlideClick(slideIndex);
      };

      this.$slick.on('afterChange', this._afterChangeHandler);
      this.$slick.on('init', this._initHandler);
      this.$slick.on('click', '.gallery-slide', this._clickHandler);
    },

    refreshCarousel() {
      if (!this.isCarouselReady) return;

      if (this._lastRefreshOrientation === this.orientation && this._refreshTimeout) {
        return;
      }

      this._refreshTimeout = this.clearTimeout(this._refreshTimeout);

      this._lastRefreshOrientation = this.orientation;

      this.$slick.slick('unslick');
      this.createSlides();
      this.$slick = window.$(this.$refs.slickContainer);
      this.$slick.slick(this.slickConfig);
      this.setupSlickEvents();

      this.requestSyncToTarget(0);

      this._refreshTimeout = setTimeout(() => {
        this._refreshTimeout = null;
      }, CAROUSEL_CONSTANTS.REFRESH_DEBOUNCE);
    },

    // USER INTERACTION HANDLERS
    handleSlideClick(slideIndex) {
      const artwork = this.filtered_artworks[slideIndex];
      if (!artwork) return;

      const activeIndex = this.ui_state.active_artwork_index;
      if (activeIndex !== null) {
        const cleanArtwork = {
          ...artwork,
          selected_variant: undefined,
          _cached_image_url: undefined
        };

        this.$store.galleryCore.selectArtwork(cleanArtwork, activeIndex);

        this.$nextTick(() => {
          this.updateSlideSelectionClasses();
          this.reinitializeSwym();
        });
      }
    },

    reinitializeSwym() {
      if (typeof window._swat?.initializeActionButtons === 'function') {
        setTimeout(() => {
          window._swat.initializeActionButtons('.gallery-builder');
        }, 100);
      }
    },

    navigateLeft() {
      if (this.$slick) {
        this.$slick.slick('slickPrev');
      }
    },

    navigateRight() {
      if (this.$slick) {
        this.$slick.slick('slickNext');
      }
    },

    // SYNC & STATE MANAGEMENT
    clearPendingSync(resetTarget = false) {
      this.pendingSyncTimeout = this.clearTimeout(this.pendingSyncTimeout);
      this.pendingSyncAttempts = 0;

      if (resetTarget) {
        this.targetArtworkId = null;
      }
    },

    queuePendingSync(delay = CAROUSEL_CONSTANTS.RETRY_DELAY_BASE) {
      if (!this.targetArtworkId) {
        return;
      }

      this.pendingSyncTimeout = this.clearTimeout(this.pendingSyncTimeout);

      const clampedDelay = Math.max(16, delay);

      this.pendingSyncTimeout = setTimeout(() => {
        this.pendingSyncTimeout = null;

        if (!this.isCarouselReady) {
          const retryDelay = Math.min(CAROUSEL_CONSTANTS.MAX_RETRY_DELAY, clampedDelay + CAROUSEL_CONSTANTS.RETRY_DELAY_BASE);
          this.queuePendingSync(retryDelay);
          return;
        }

        const synced = this.performCarouselSync(this.targetArtworkId);
        if (synced) {
          this.pendingSyncAttempts = 0;
          return;
        }

        if (this.pendingSyncAttempts >= this.maxPendingSyncAttempts) {
          return;
        }

        this.pendingSyncAttempts += 1;
        const retryDelay = Math.min(CAROUSEL_CONSTANTS.MAX_RETRY_DELAY, CAROUSEL_CONSTANTS.RETRY_DELAY_BASE * (this.pendingSyncAttempts + 1));
        this.queuePendingSync(retryDelay);
      }, clampedDelay);
    },

    requestSyncToTarget(initialDelay = CAROUSEL_CONSTANTS.RETRY_DELAY_BASE) {
      if (!this.targetArtworkId) {
        return;
      }

      this.pendingSyncAttempts = 0;
      this.queuePendingSync(initialDelay);
    },

    performCarouselSync(artworkId) {
      if (!artworkId) {
        return false;
      }

      if (!this.isCarouselReady) {
        return false;
      }

      const slideIndex = this.findSlideIndexByArtworkId(artworkId);

      if (slideIndex === -1) {
        return false;
      }

      const currentSlide = this.$slick.slick('slickCurrentSlide');

      if (currentSlide === slideIndex && this._pendingSyncSlide === null) {
        return true;
      }

      if (this._pendingSyncSlide === slideIndex) {
        return true;
      }

      if (currentSlide !== slideIndex) {
        this._pendingSyncSlide = slideIndex;
        this.$slick.slick('slickGoTo', slideIndex);
      }

      return true;
    },

    syncCarouselWithGallery() {
      const activeIndex = this.ui_state.active_artwork_index;

      if (activeIndex === null) {
        this.clearPendingSync(true);
        return;
      }

      const selectedArtwork = this.selected_artworks[activeIndex];
      const targetId = selectedArtwork?.id;

      if (!targetId) {
        this.clearPendingSync(true);
        return;
      }

      this.targetArtworkId = targetId;

      if (!this.isCarouselReady) {
        this.requestSyncToTarget();
        return;
      }

      this.updateSlideSelectionClasses();

      const syncResult = this.performCarouselSync(targetId);

      if (syncResult) {
        this.clearPendingSync(false);
      } else {
        this.requestSyncToTarget();
      }
    },

    // LIFECYCLE METHODS
    init() {
      const isMobile = window.innerWidth <= 989;

      // Skip initialization on mobile devices
      if (isMobile) {
        return;
      }

      this.$watch('filtered_artworks', (newArtworks, oldArtworks) => {
        if (newArtworks.length > 0 && !this.isInitialized) {
          setTimeout(() => {
            this.initSlick();
          }, CAROUSEL_CONSTANTS.INIT_DELAY);
        } else if (this.isInitialized) {
          const needsRefresh = !oldArtworks ||
                               newArtworks.length !== oldArtworks.length ||
                               JSON.stringify(newArtworks.map(a => a?.id)) !== JSON.stringify(oldArtworks.map(a => a?.id));

          if (needsRefresh) {
            this.refreshCarousel();
          }
        }
      }, { immediate: true, deep: false });

      this.$watch('orientation', () => {
        if (this.isInitialized) {
          this.refreshCarousel();
        }
      });

      this.$watch('$store.galleryUI.active_artwork_index', () => {
        this.syncCarouselWithGallery();
      });

      this.$watch('selected_artworks', () => {
        this.syncCarouselWithGallery();
      }, { deep: true });

      this._productsUpdatedHandler = (event) => {
        const source = event.detail?.source || null;
        const shouldForceRefresh = source === 'search';

        setTimeout(() => {
          if (!this.isCarouselReady) {
            return;
          }

          if (shouldForceRefresh) {
            this.refreshCarousel();
            return;
          }

          const targetLength = Array.isArray(this.filtered_artworks)
            ? this.filtered_artworks.length
            : 0;
          const slickInstance = this.slickInstance;
          const currentSlideCount = slickInstance ? slickInstance.slideCount : 0;

          if (currentSlideCount !== targetLength) {
            this.refreshCarousel();
          }
        }, CAROUSEL_CONSTANTS.INIT_DELAY);
      };
      document.addEventListener('gallery:products-updated', this._productsUpdatedHandler);

      if (!this._primeHandler) {
        this._primeHandler = (event) => {
          const detail = event?.detail || {};
          const forceRefresh = Boolean(detail.force);
          const hasArtworks = Array.isArray(this.filtered_artworks) && this.filtered_artworks.length > 0;

          if (this.isInitialized) {
            if (forceRefresh && hasArtworks) {
              this.refreshCarousel();
            } else if (this.targetArtworkId && hasArtworks) {
              this.requestSyncToTarget(0);
            }
            return;
          }

          const schedule = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
            ? window.requestIdleCallback
            : (fn) => setTimeout(fn, 0);

          schedule(() => {
            if (this.isInitialized) {
              return;
            }

            const available = Array.isArray(this.filtered_artworks) && this.filtered_artworks.length > 0;
            if (available) {
              this.initSlick();
            }
          });
        };

        this.$el.addEventListener('gallery:prime-carousel', this._primeHandler);
      }
    },

    destroy() {
      if (this.imageObserver) {
        this.imageObserver.disconnect();
        this.imageObserver = null;
      }

      if (this._productsUpdatedHandler) {
        document.removeEventListener('gallery:products-updated', this._productsUpdatedHandler);
        this._productsUpdatedHandler = null;
      }

      if (this.$slick) {
        if (this._afterChangeHandler) {
          this.$slick.off('afterChange', this._afterChangeHandler);
        }
        if (this._initHandler) {
          this.$slick.off('init', this._initHandler);
        }
        if (this._clickHandler) {
          this.$slick.off('click', '.gallery-slide', this._clickHandler);
        }

        try {
          this.$slick.slick('unslick');
        } catch (e) {
        }

        this.$slick = null;
        this._afterChangeHandler = null;
        this._initHandler = null;
        this._clickHandler = null;
        this.isInitialized = false;
      }

      this.clearPendingSync(true);

      if (this._primeHandler) {
        this.$el.removeEventListener('gallery:prime-carousel', this._primeHandler);
        this._primeHandler = null;
      }
    }
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('galleryCarousel', galleryCarousel);
});